import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { without } from 'lodash';

import { Dropdown, SearchOption } from '@app/components';
import { useFeatureFlag } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { Feature } from '@app/constants';

import css from './SearchOptionsDropdown.module.scss';

interface SearchOptionsDropdownProps {
  option: string;
  onSelect: (searchOption: string) => void;
}

const initialSearchOptions = [
  SearchOption.keyword,
  SearchOption.title,
  SearchOption.author,
  SearchOption.issn,
  SearchOption.isbn,
  SearchOption.lccn,
  SearchOption.subject,
  SearchOption.advanced,
  SearchOption.query,
];

export const SearchOptionsDropdown: FC<SearchOptionsDropdownProps> = ({
  option,
  onSelect,
}) => {
  const { $t } = useIntl();
  const LCCNSearchFlag = useFeatureFlag({ name: Feature.LCCNSearch });

  const searchOptions = LCCNSearchFlag.isActive
    ? initialSearchOptions
    : without(initialSearchOptions, SearchOption.lccn);

  const selectedOption = useMemo(
    () => ({
      label: $t(sharedMessages[option]),
      value: option,
    }),
    [$t, option]
  );

  const options = useMemo(
    () =>
      searchOptions.map(searchOption => ({
        label: $t(sharedMessages[searchOption]),
        value: searchOption,
      })),
    [$t, searchOptions]
  );

  const handleSelectedItemChange = useCallback(
    ({ selectedItem }) => {
      onSelect(selectedItem);
    },
    [onSelect]
  );

  return (
    <Dropdown
      id="searchOptionsDropdown"
      items={options}
      label={$t({
        id: 'searchOptionsDropdown.label',
        defaultMessage: 'Search option',
      })}
      hideLabel
      className={css.searchOptionsDropdown}
      selectedItem={selectedOption}
      onSelectedItemChange={handleSelectedItemChange}
    />
  );
};
